export interface StatsigAppUser {
  readonly email?: string;
  readonly id: string;
  readonly selectedOrganizationRole?: {
    id: string;
    organization?: {
      id: string;
      name: string;
      type: string;
    } | null;
    role: string;
  } | null;
}

export enum StatsigDynamicConfigName {
  AddressValidator = 'address_validator',
  CohortLearnerMetabaseReport = 'cohort_learner_metabase_report',
  CohortLearnerThoughtSpotReport = 'cohort_learner_thoughtspot_report',
  MetabaseDashboardId = 'metabase_dashboard_id',
  MetabaseReportInventory = 'metabase_report_inventory',
  ThoughtSpotDashboardId = 'thoughtspot_dashboard_id',
  ThoughtSpotReportInventory = 'thoughtspot_report_inventory',
  VelocityGenerateOffersConfig = 'velocity_generate_offers_config',
  VelocityMockMake = 'velocity_mock_make',
}

export enum FeatureFlagId {
  /**
   * Controls visibility of the evaluator picker on the activity submission screen.
   * Flag ON renders the picker. Flag OFF hides the picker. Form schema, defaults,
   * validation, and mutation payload are identical in both states.
   */
  ActivityEvaluatorSelection = 'activity_evaluator_selection',
  AiRubricImport = 'ai_rubric_import',
  AnalyticsConnection = 'analytics_connection',
  ClearedStatusUx = 'cleared_status_ux',
  CredentialCheckKillswitch = 'credential_check_killswitch',
  DashboardMetabase = 'dashboard_metabase',
  FileOrLinkAttachment = 'file_or_link_attachment',
  FundingTrackingLearnerLog = 'orange_fundingtracking_learnerlog',
  InAppCanvasExperience = 'in_app_canvas_experience',
  /**
   * Controls whether or not users are presented with an interruption dialog on first login that
   * prompts them to complete their profile information before allowing them to continue into the
   * application.
   *
   * This will only occur for learners that do not have a valid value for their DOB, Address and/or
   * Phone Number (note that learners can simply check the checkboxes indicating that they do not\
   * have a phone number or do not have an address as well).
   *
   * This dialog flow will be presented immediately after the policy acceptance flow if the learner
   * has also not yet accepted terms of use policies.
   */
  InterruptionDialogFlow = 'interruption_dialog_flow',
  LearnerEvaluatorSelectionTt = 'learner_evaluator_selection_tt',
  LevelLocking = 'level_locking',
  Lti = 'lti',
  MmfCredentialing = 'mmf_credentialing',
  MultiEmailWrite = 'multi_email_write',
  /**
   * Controls whether or not users that have not yet accepted the company policies for using the
   * application will be presented with a dialog to accept those policies when they first login
   * (or anytime they visit a page without having yet accepted the policies).
   *
   * This is primarily being used as a fail safe switch, just in case there is a bug with the policy
   * acceptance flow.  This is because if there is a bug with the policy acceptance flow, it can
   * prevent users from being able to access the application entirely.
   *
   * DO NOT TURN THIS FLAG OFF UNLESS THIS HAS BEEN COMMUNICATED VERY CLEARLY WITH LEADERSHIP, BOTH
   * IN PRODUCT AND ENGINEERING.
   */
  PolicyAcceptanceFlow = 'policy_acceptance_flow',
  ProgramExternalIds = 'program_external_ids_feature_flag',
  ProgramIndustryOccupationCodes = 'program_industry_occupation_codes',
  ProgramPercentageIndicators = 'program_percentage_indicators',
  ProgramTimeTracking = 'program_time_tracking',
  RubricExternalIds = 'rubric_external_ids_feature_flag',
  RudderstackEventEmitter = 'rudderstack_event_emitter',
  SkillsObservationTracking = 'skills_observation_tracking',
  SkillsObservationTrackingInstructors = 'skills_observation_tracking_instructors',
  SwitchableEmails = 'switchable_emails',
  ThoughtSpotEmbed = 'thoughtspot_embed',
  UnreachableAccounts = 'unreachable_accounts',
  VelocityIntegrationKillswitch = 'velocity_integration_killswitch',
  WageTrackingLearnerLog = 'orange_wagetracking_learnerlog',
}
